import type { IUserInfo } from "@/account/account-models";
import type { IFileWrapper } from "@/common/uploader/uploader-helpers";
import type { IMediaType } from "@/models";

export interface IChatInfo {
  available: boolean;
  created_at: string;
  created_under_force_free_chats: boolean;
  credit_rate: number;
  new_rate: number; // the new credit_rate that will be applied after user replies
  credit_rate_updated_at: string;
  intro_ended: boolean;
  is_affiliate_chat: boolean;
  is_free_for_subscriber: boolean;
  is_hidden: boolean;
  is_intro: boolean;
  is_intro_ended?: boolean; //TODO: Check if exists
  is_new_fan?: boolean;
  is_tips_enabled?: boolean;
  last_message_id: string;
  last_reacted_message_id: string | null;
  last_message_text: string;
  last_message_time: string;
  other_user: IUserInfo;
  recipient_id: string;
  sender_id: string;
  unread_messages: number;
  unread_tip: boolean | null;
  _id: string;
  updateFn?: () => void;
  favorite?: boolean;
  media_blocked_from?: boolean; // former chat.other_user.media_blocked_from (other user has blocked you from sending media or not enough messages has been exchanged yet)
  is_media_blocked_temporarily?: boolean; // need to exchange more messages (at least 10 unique replies from model)
  media_blocked_for?: boolean; // former chat.other_user.media_blocked_for (you blocked other user from sending you media)
  is_muted?: boolean;
  notes: {
    _id: string;
    text: string;
    created_at: string;
  }[];
  // Paid Chat v2 fields
  auto_unlock?: boolean; // fan's toggle: true = Active (auto-charge), false = Off (blur incoming texts)
  is_paid_chat?: boolean; // derived: credit_rate > 0 && !is_free_for_subscriber
  locked_messages_total?: number; // credits needed to unlock all locked messages
  paid_chat_request?: { rate: number; status: "pending" } | null;
  is_call_blocked?: boolean; // other user blocked calls in this chat
  call_blocked_by_me?: boolean; // current user blocked incoming calls from this chat
  call?: IChatCallInfo;
}

export interface IChatCallInfo {
  rate: number; // per-minute rate that applies to the next call in this chat
  custom_rate: number | null; // creator's per-chat override, null = global rate
  // "usd": the fan pays the call by card holds, not credits.
  billing_method?: "credits" | "usd";
  rate_cents?: number; // price per minute, when paid by card
  hold_cents?: number; // one hold, when paid by card
}

// export interface IChatCategories {
//   all?: IChatInfo[];
//   fans?: IChatInfo[];
//   subscribers?: IChatInfo[];
//   favorites?: IChatInfo[];
//   unreplied?: IChatInfo[];
//   recent?: IChatInfo[];
//   vip?: IChatInfo[];
// }

export interface IChatTotals {
  all: number;
  online: number;
  fans: number;
  paid_chat: number;
  followers: number;
  following: number;
  subscribers: number;
  subscribed: number;
  favorites: number;
  unreplied: number;
  unread: number;
  vip: number;
  recent: number;
  sent: number;
  hidden: number;
  // the API only sends this to creators
  creators: number;
}

export enum EPatchUpdateChatsAction {
  READ = "read",
  DELETE = "delete",
  HIDE = "hide",
  UNHIDE = "unhide",
  FAVORITE = "favorite",
  REMOVE_FAVORITE = "remove_favorite",
}

export const MAX_FREE_CHATS_ERROR_MESSAGE =
  "You have hit your maximum amount of free chats, buy a credit package to unlock unlimited free chats.";
export const DENIED_FREE_CHAT_ERROR_MESSAGE =
  "You need to buy a credit package to access chats with this member.";
export const MAX_CHATS_CREATED_ERROR_MESSAGE =
  "You have reached the maximum number of chats you can create. Please try again later.";

export enum EBroadcastMessageType {
  Text = "text",
  Image = "image",
  Video = "video",
  Gif = "gif",
}

export interface IBroadcastMessage {
  _id?: string;
  type: EBroadcastMessageType;
  text?: string;
  gif_url?: string;
  width?: number;
  height?: number;
  public_id?: string;
  media_id?: string;
  media?: IMediaType;
  mediaFileWrapper?: IFileWrapper | null;
  price?: number;
  price_in_dollars?: boolean; // price in usd instead of Credits
  self_destruct?: boolean;
  self_destruct_hours?: number | null;
  __clientSideSendId?: boolean | string;
  __isUploadFailed?: boolean;
  __errorMessage?: string;
  __isBeingDeleted?: boolean;
  __isVaultMedia?: boolean;
}

export enum EBroadcastStage {
  None = "None",
  Uploading = "Uploading",
  Confirming = "Confirming",
  Broadcasting = "Broadcasting",
  Banned = "Banned",
  Error = "Error",
}
